import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import Layout from '../components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PhoneCall, TrendingUp, Upload } from 'lucide-react';

const ReviewerDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const permissions = user?.permissions || [];
  const canUpload = permissions.includes('upload_calls');
  const canViewReports = permissions.includes('view_reports');
  const canAudit = permissions.includes('conduct_audits');

  useEffect(() => {
    fetchCalls();
  }, []);

  const fetchCalls = async () => {
    try {
      const response = await axios.get(`${API}/calls`);
      setCalls(response.data);
    } catch (error) {
      console.error('Failed to fetch calls:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-amber-50 text-amber-700 border-amber-200',
      transcribed: 'bg-blue-50 text-blue-700 border-blue-200',
      assessed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    };
    return badges[status] || badges.pending;
  };

  return (
    <Layout>
      <div className="space-y-8 fade-in" data-testid="reviewer-dashboard">
        {/* Header */}
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Welcome, {user?.name}
          </h1>
          <p className="text-slate-600">Review call recordings and assess compliance</p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {canUpload && (
            <Card className="p-6 border border-indigo-200 bg-indigo-50 shadow-sm">
              <Upload size={32} className="text-indigo-600 mb-3" />
              <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Upload New Call
              </h3>
              <p className="text-slate-600 mb-4">Upload audio files for transcription and assessment</p>
              <Button
                onClick={() => navigate('/upload')}
                className="bg-[#4338CA] hover:bg-[#3730A3] text-white"
                data-testid="upload-new-call-button"
              >
                Upload Call
              </Button>
            </Card>
          )}
          {canViewReports && (
            <Card className="p-6 border border-emerald-200 bg-emerald-50 shadow-sm">
              <TrendingUp size={32} className="text-emerald-600 mb-3" />
              <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                View Analytics
              </h3>
              <p className="text-slate-600 mb-4">Track performance trends and insights</p>
              <Button
                onClick={() => navigate('/analytics')}
                variant="outline"
                className="border-emerald-600 text-emerald-700 hover:bg-emerald-100"
                data-testid="view-analytics-button"
              >
                View Analytics
              </Button>
            </Card>
          )}
        </div>

        {/* Calls List */}
        <Card className="p-6 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Call Recordings
            </h2>
            <Button onClick={fetchCalls} variant="outline" size="sm" data-testid="refresh-calls-button">
              Refresh
            </Button>
          </div>

          {loading ? (
            <p className="text-slate-600">Loading calls...</p>
          ) : calls.length > 0 ? (
            <div className="space-y-3">
              {calls.map((call) => (
                <div
                  key={call.id}
                  className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                  data-testid={`call-item-${call.id}`}
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className="p-3 bg-indigo-50 rounded-lg">
                      <PhoneCall size={24} className="text-indigo-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{call.title}</p>
                      <p className="text-sm text-slate-600">
                        Uploaded {new Date(call.uploaded_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadge(call.status)}`}
                      data-testid={`call-status-${call.id}`}
                    >
                      {call.status}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        if (call.status === 'assessed') {
                          navigate(`/assessment/${call.id}`);
                        } else {
                          navigate('/upload', { state: { callId: call.id } });
                        }
                      }}
                      disabled={call.status !== 'assessed' && !canUpload}
                      data-testid={`view-call-${call.id}`}
                    >
                      {call.status === 'assessed' ? 'View Results' : 'Process'}
                    </Button>
                    {canAudit && (
                      <Button
                        size="sm"
                        className="bg-[#4338CA] hover:bg-[#3730A3] text-white"
                        onClick={() => navigate(`/conduct-audit/${call.id}`)}
                        data-testid={`audit-call-${call.id}`}
                      >
                        Audit
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <PhoneCall size={48} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-600 mb-4">No call recordings yet</p>
              {canUpload && (
                <Button onClick={() => navigate('/upload')} data-testid="upload-first-call-button">
                  Upload First Call
                </Button>
              )}
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
};

export default ReviewerDashboard;